import { useState } from 'react';

import {
  Callout,
  Chip,
  Confirm,
  Empty,
  Field,
  Loading,
  Panel,
  TableScroll,
  TextInput,
  explainFailure,
  formatDateTime,
  useLoad,
} from '@affdice/portal-kit';

import type { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { alreadyDecided, askedAt, isOwnRequest } from './identity-approvals.ts';

type Status = 'PENDING' | 'APPROVED' | 'REJECTED';

const FILTERS: readonly { id: Status; label: 'aff.approvals.filter.pending' | 'aff.approvals.filter.approved' | 'aff.approvals.filter.rejected' }[] = [
  { id: 'PENDING', label: 'aff.approvals.filter.pending' },
  { id: 'APPROVED', label: 'aff.approvals.filter.approved' },
  { id: 'REJECTED', label: 'aff.approvals.filter.rejected' },
];

interface Row {
  readonly id: string;
  readonly operator_name: string | null;
  readonly status: string;
  readonly requested_by: string;
  readonly requested_by_email: string | null;
  readonly expired: boolean;
}

/**
 * Identity merges waiting for a second member of platform staff.
 * The person who asked sees that it is waiting; they do not get the buttons.
 */
export function IdentityApprovals({
  api, canEdit, currentUserId,
}: {
  api: PlatformApi;
  canEdit: boolean;
  currentUserId?: string;
}) {
  const [status, setStatus] = useState<Status>('PENDING');
  const [version, setVersion] = useState(0);
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [deciding, setDeciding] = useState<
    { id: string; decision: 'APPROVE' | 'REJECT'; who: string } | null
  >(null);
  const loaded = useLoad(() => api.identityConsolidations(status), [status, version]);
  const items: readonly Row[] = loaded.state === 'ready' ? loaded.value.items : [];

  const empty = status === 'APPROVED'
    ? t('aff.approvals.empty.approved')
    : status === 'REJECTED'
      ? t('aff.approvals.empty.rejected')
      : t('aff.approvals.empty.pending');

  return (
    <Panel title={t('aff.approvals.title')}>
      <p className="faint">{t('aff.approvals.intro')}</p>
      <div className="form-actions" role="group" aria-label={t('aff.approvals.title')}>
        {FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={status === filter.id ? 'primary' : 'copy'}
            aria-pressed={status === filter.id}
            onClick={() => { setStatus(filter.id); setNotice(null); }}
          >
            {t(filter.label)}
          </button>
        ))}
      </div>
      {notice ? <Callout fold={false}>{notice}</Callout> : null}
      {loaded.state === 'loading' ? <Loading rows={3} /> : null}
      {loaded.state === 'error' ? <Callout tone="error">{explainFailure(loaded.error).message}</Callout> : null}
      {loaded.state === 'ready' && items.length === 0 ? <Empty>{empty}</Empty> : null}
      {loaded.state === 'ready' && items.length > 0 ? (
        <TableScroll>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('aff.approvals.col.who')}</th>
                <th scope="col">{t('aff.approvals.col.what')}</th>
                <th scope="col" className="num">{t('aff.approvals.col.amount')}</th>
                <th scope="col">{t('aff.approvals.col.when')}</th>
                <th scope="col"><span className="sr-only">{t('aff.approvals.actions')}</span></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const own = isOwnRequest(row.requested_by, currentUserId);
                const open = status === 'PENDING' && !row.expired;
                const at = askedAt(row.id);
                const what = row.operator_name
                  ? `${t('aff.approvals.what')} — ${row.operator_name}`
                  : t('aff.approvals.what');
                return (
                  <tr key={row.id}>
                    <td>{row.requested_by_email || t('aff.approvals.unknownWho')}</td>
                    <td>{what}</td>
                    <td className="num">{t('aff.approvals.noAmount')}</td>
                    <td>{at ? formatDateTime(at.toISOString()) : t('aff.approvals.whenUnknown')}</td>
                    <td className="num">
                      {row.expired && status === 'PENDING' ? <Chip tone="attention">{t('aff.approvals.expired')}</Chip> : null}
                      {open && own ? <span className="faint">{t('aff.approvals.own')}</span> : null}
                      {open && !own && !canEdit ? <span className="faint">{t('aff.approvals.notYours')}</span> : null}
                      {open && !own && canEdit ? (
                        <div className="form-actions">
                          <button
                            type="button"
                            className="primary"
                            onClick={() => { setReason(''); setDeciding({ id: row.id, decision: 'APPROVE', who: row.requested_by_email || t('aff.approvals.unknownWho') }); }}
                          >
                            {t('aff.approvals.approve')}
                          </button>
                          <button
                            type="button"
                            className="copy"
                            onClick={() => { setReason(''); setDeciding({ id: row.id, decision: 'REJECT', who: row.requested_by_email || t('aff.approvals.unknownWho') }); }}
                          >
                            {t('aff.approvals.reject')}
                          </button>
                        </div>
                      ) : null}
                      {status === 'APPROVED' ? <Chip tone="locked">{t('aff.approvals.filter.approved')}</Chip> : null}
                      {status === 'REJECTED' ? <Chip tone="attention">{t('aff.approvals.filter.rejected')}</Chip> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      ) : null}
      {deciding ? (
        <Confirm
          title={deciding.decision === 'APPROVE' ? t('aff.approvals.confirmApprove') : t('aff.approvals.confirmReject')}
          tone={deciding.decision === 'APPROVE' ? 'caution' : 'error'}
          confirmLabel={deciding.decision === 'APPROVE' ? t('aff.approvals.approve') : t('aff.approvals.reject')}
          body={
            <>
              <p>{t('aff.approvals.what')}</p>
              <p>{t('aff.approvals.noAmount')}</p>
              <p>{deciding.who}</p>
              <Field label={t('aff.approvals.reason')} hint={t('aff.approvals.reasonHint')}>
                <TextInput value={reason} onChange={setReason} />
              </Field>
            </>
          }
          onCancel={() => setDeciding(null)}
          onConfirm={async () => {
            if (reason.trim().length < 5) throw new Error(t('aff.approvals.reasonShort'));
            try {
              if (deciding.decision === 'APPROVE') await api.approveIdentityConsolidation(deciding.id, reason.trim());
              else await api.rejectIdentityConsolidation(deciding.id, reason.trim());
              setNotice(deciding.decision === 'APPROVE' ? t('aff.approvals.applied') : t('aff.approvals.rejectedDone'));
            } catch (error) {
              if (!alreadyDecided(error)) throw error;
              setNotice(t('aff.approvals.alreadyDecided'));
            }
            setDeciding(null);
            setVersion((v) => v + 1);
          }}
        />
      ) : null}
    </Panel>
  );
}
